import React, { useState } from "react";
import { fetchDataFromFirebase } from "../DatabaseFirebase/firebaseService";
import { invalidateLabelCache, setEntityLabelsSource } from "../utils/labelMapping";


export function getSharedReportIdentity(user, selectedCustomerId) {
  const params = new URLSearchParams(window.location.search);
  const urlUserId = params.get("user_id");
  const urlCustomerId = params.get("customer_id");
  return {
    userId: urlUserId ? parseInt(urlUserId, 10) : user?.user_role?.user_id,
    customerId: urlCustomerId ? parseInt(urlCustomerId, 10) : (selectedCustomerId ?? user?.customer_id),
  };
}

export const AuthContext = React.createContext({
  token: null,
  setUserToken: (token) => {},
  clearSession: () => {},
  user: null,
  customerUiInfo: null,
  userCustomerId: null,
  selectedCustomerId: null,
  setSelectedCustomer: () => {},
  setSelectedCustomerById: () => {},
});

const AuthContextProvider = (props) => {
  const [token, setToken] = useState(localStorage.getItem("token") || null);

  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(
    JSON.parse(localStorage.getItem("isAdmin")) || false
  );

  const [authUserEmail, setAuthUserEmail] = useState(
    localStorage.getItem("authEmail") || ""
  );

  // Shared URL-param helpers — computed once per render but only the first render matters
  // for the lazy useState initializers below.
  const _urlParams = new URLSearchParams(window.location.search);
  const _urlCid = _urlParams.get("customer_id");
  const _urlUserId = _urlParams.get("user_id");
  const _storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const _groupCustomers = JSON.parse(localStorage.getItem("group_customers") || "[]");
  // Only honour the URL's customer_id when the URL's user_id matches the logged-in user
  const _isSameUser = !!(_urlCid && _urlUserId &&
    String(_storedUser?.user_role?.user_id) === String(_urlUserId));
  // When a different user's URL is pasted and this user has multi-industry, fall back to first industry
  const _isDifferentUserMultiIndustry = !!(_urlCid && _urlUserId && !_isSameUser && _groupCustomers.length >= 2);
  const _effectiveCid = _isSameUser
    ? parseInt(_urlCid, 10)
    : _isDifferentUserMultiIndustry
    ? _groupCustomers[0].id
    : null;

  const [loggedInUser, setLoggedInUser] = useState(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    let selectedId;
    if (_effectiveCid !== null) {
      selectedId = _effectiveCid;
      localStorage.setItem("selected_customer_id", String(_effectiveCid));
    } else {
      selectedId = localStorage.getItem("selected_customer_id")
        ? parseInt(localStorage.getItem("selected_customer_id"), 10)
        : null;
    }
    if (storedUser && selectedId) {
      const updated = { ...storedUser, customer_id: selectedId };
      // Only persist user object change when it's the same user's URL
      if (_isSameUser) localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    }
    return storedUser;
  });

  const [firebaseAuthUserData, setFirebaseAuthUserData] = useState(
    JSON.parse(localStorage.getItem("firebaseUser"))
  );

  const [customerUiInfo, setCustomerUiInfo] = useState(() => {
    if (_effectiveCid !== null) {
      const entry = _groupCustomers.find((c) => c.id === _effectiveCid);
      if (entry) {
        const uiInfo = entry.customer_ui_info || null;
        localStorage.setItem("customer_ui_info", JSON.stringify(uiInfo));
        invalidateLabelCache();
        setEntityLabelsSource(uiInfo);
        return uiInfo;
      }
    }
    const stored = localStorage.getItem("customer_ui_info");
    const uiInfo = stored ? JSON.parse(stored) : null;
    setEntityLabelsSource(uiInfo);
    return uiInfo;
  });

  // Permanent customer ID set at login — never changes during session
  const [userCustomerId, setUserCustomerId] = useState(() => {
    const stored = localStorage.getItem("user_customer_id");
    return stored ? parseInt(stored, 10) : null;
  });

  // Currently active customer — can change when user switches industry
  const [selectedCustomerId, setSelectedCustomerIdState] = useState(() => {
    if (_effectiveCid !== null) return _effectiveCid;
    const saved = localStorage.getItem("selected_customer_id");
    const base = localStorage.getItem("user_customer_id");
    if (saved) return parseInt(saved, 10);
    if (base) return parseInt(base, 10);
    return null;
  });

  const setUser = (token, loggedInUserEmail, user) => {
    fetchDataFromFirebase((data) => {
      if (data !== null) {
        const users = Object.values(data);
        const authUser = users.filter((u) => u.email === loggedInUserEmail);
        setFirebaseAuthUserData(authUser);
        localStorage.setItem("firebaseUser", JSON.stringify(authUser));
        if (authUser && authUser.length !== 0 && authUser[0]?.role === "admin") {
          setIsCurrentUserAdmin(true);
          localStorage.setItem("isAdmin", true);
        } else {
          setIsCurrentUserAdmin(false);
          localStorage.setItem("isAdmin", false);
        }
      }
    }, "Users");

    localStorage.setItem("token", token);
    const expiration = new Date();
    expiration.setHours(expiration.getHours() + 1);
    localStorage.setItem("expiration", expiration.toISOString());

    localStorage.setItem("authEmail", loggedInUserEmail);
    setToken(token);
    setAuthUserEmail(loggedInUserEmail);
    localStorage.setItem("user", JSON.stringify(user));
    setLoggedInUser(user);

    const uiInfo = user?.customer_ui_info || null;
    setCustomerUiInfo(uiInfo);
    localStorage.setItem("customer_ui_info", JSON.stringify(uiInfo));
    invalidateLabelCache();
    setEntityLabelsSource(uiInfo);

    // Store permanent customer ID (set once, never changes in session)
    const cid = user?.customer_id;
    if (cid) {
      localStorage.setItem("user_customer_id", String(cid));
      setUserCustomerId(cid);

      // if (!localStorage.getItem("selected_customer_id")) {
      //   localStorage.setItem("selected_customer_id", String(cid));
      //   setSelectedCustomerIdState(cid);
      // }

      localStorage.setItem("selected_customer_id", String(cid));
      setSelectedCustomerIdState(cid);
    }
  };

  // Call when the user picks a different industry from the navbar dropdown
  const setSelectedCustomer = (entry) => {
    const newId = entry.id;
    const newUiInfo = entry.customer_ui_info || null;
    setSelectedCustomerIdState(newId);
    localStorage.setItem("selected_customer_id", String(newId));
    setCustomerUiInfo(newUiInfo);
    localStorage.setItem("customer_ui_info", JSON.stringify(newUiInfo));
    invalidateLabelCache();
    setEntityLabelsSource(newUiInfo);
    // Keep user.customer_id in sync so all existing code using user.customer_id picks up the selection
    setLoggedInUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, customer_id: newId };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  };

  const setSelectedCustomerById = (id) => {
    const newId = Number(id);
    const stored = localStorage.getItem("group_customers");
    const groupCustomers = stored ? JSON.parse(stored) : [];
    const entry = groupCustomers.find((c) => c.id === newId);
    setSelectedCustomerIdState(newId);
    localStorage.setItem("selected_customer_id", String(newId));
    const newUiInfo = entry?.customer_ui_info || null;
    setCustomerUiInfo(newUiInfo);
    localStorage.setItem("customer_ui_info", JSON.stringify(newUiInfo));
    invalidateLabelCache();
    setEntityLabelsSource(newUiInfo);
    setLoggedInUser((prev) => {
      if (!prev) return prev;
      if (prev.customer_id === newId) return prev; // no-op — avoids extra fetchuserSet trigger
      const updated = { ...prev, customer_id: newId };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  };

  const clearSession = () => {
    setToken(null);
    setLoggedInUser(null);
    setFirebaseAuthUserData(null);
    setIsCurrentUserAdmin(false);
    setAuthUserEmail("");
    setCustomerUiInfo(null);
    setUserCustomerId(null);
    setSelectedCustomerIdState(null);
    localStorage.clear();
    sessionStorage.clear();
    invalidateLabelCache();
    setEntityLabelsSource(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        setUser,
        clearSession,
        isCurrentUserAdmin,
        authUserEmail,
        user: loggedInUser,
        loggedInUser: firebaseAuthUserData,
        customerUiInfo,
        userCustomerId,
        selectedCustomerId,
        setSelectedCustomer,
        setSelectedCustomerById,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
